/**
 * cart-unique-items.js
 *
 * Prevents adding the same product variant to the cart more than once.
 * Digital products (PSD mockups) — qty > 1 makes no sense.
 *
 * UX:
 *  - On page load + cart updates, if the variant is already in the cart,
 *    the ADD TO CART button switches to "Already in your cart" with bg #C97064.
 *  - Clicks on a marked button are silently blocked.
 *  - Fetch to /cart/add is also silently short-circuited as a backstop
 *    for quick-add / sticky add-to-cart paths.
 */
(function () {
  if (window.__cartUniqueItemsInstalled) return;
  window.__cartUniqueItemsInstalled = true;

  const IN_CART_TEXT = 'Already in your cart';
  const IN_CART_CLASS = 'in-cart';

  const originalFetch = window.fetch.bind(window);

  function getUrlString(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input === 'object' && 'url' in input) return input.url;
    return '';
  }

  function isCartAddUrl(url) {
    return /\/cart\/add(\.js)?(\?|$)/.test(url || '');
  }

  function isCartMutationUrl(url) {
    return /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/.test(url || '');
  }

  function extractVariantId(init) {
    if (!init || !init.body) return null;
    const body = init.body;

    if (typeof body === 'string') {
      try {
        const parsed = JSON.parse(body);
        if (parsed && parsed.id != null) return String(parsed.id);
        if (parsed && Array.isArray(parsed.items) && parsed.items[0]?.id != null) {
          return String(parsed.items[0].id);
        }
      } catch {
        // fallthrough to URL-encoded
      }
      try {
        const params = new URLSearchParams(body);
        const id = params.get('id');
        if (id) return String(id);
      } catch {
        /* ignore */
      }
    }

    if (typeof FormData !== 'undefined' && body instanceof FormData) {
      const id = body.get('id');
      if (id) return String(id);
    }

    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
      const id = body.get('id');
      if (id) return String(id);
    }

    return null;
  }

  let cachedCartIds = null;
  let cartFetchInflight = null;

  async function fetchCartVariantIds() {
    if (cartFetchInflight) return cartFetchInflight;
    cartFetchInflight = (async () => {
      try {
        const res = await originalFetch('/cart.js', { credentials: 'same-origin' });
        if (!res.ok) return new Set();
        const cart = await res.json();
        if (!cart || !Array.isArray(cart.items)) return new Set();
        return new Set(cart.items.map((item) => String(item.variant_id)));
      } catch {
        return new Set();
      }
    })();
    try {
      const ids = await cartFetchInflight;
      cachedCartIds = ids;
      return ids;
    } finally {
      cartFetchInflight = null;
    }
  }

  function getCachedCartIds() {
    return cachedCartIds || new Set();
  }

  async function getCartVariantIds() {
    if (cachedCartIds) return cachedCartIds;
    return fetchCartVariantIds();
  }

  function getButtonVariantId(btn) {
    const form = btn.closest('form[action*="/cart/add"]');
    if (!form) return null;
    const input = form.querySelector('[name="id"]');
    return input ? String(input.value) : null;
  }

  function getTextNode(btn) {
    // Find the deepest span/element whose immediate text content is the visible label.
    const candidates = btn.querySelectorAll('span, div');
    for (const el of candidates) {
      if (el.querySelector('svg')) continue;
      const txt = el.textContent && el.textContent.trim();
      if (txt && txt.length < 64) return el;
    }
    return null;
  }

  function applyState(btn, inCart) {
    const isQuickAdd = btn.classList.contains('quick-add__button');
    if (inCart) {
      btn.classList.add(IN_CART_CLASS);
      btn.setAttribute('aria-disabled', 'true');
      if (!isQuickAdd) {
        if (!btn.dataset.originalLabel) {
          const node = getTextNode(btn);
          if (node) {
            btn.dataset.originalLabel = node.textContent;
            node.textContent = IN_CART_TEXT;
          }
        }
      }
    } else {
      if (!isQuickAdd && btn.dataset.originalLabel) {
        const node = getTextNode(btn);
        if (node) node.textContent = btn.dataset.originalLabel;
        delete btn.dataset.originalLabel;
      }
      btn.classList.remove(IN_CART_CLASS);
      btn.removeAttribute('aria-disabled');
    }
  }

  let refreshTimer = null;
  let refreshInflight = false;
  let refreshPending = false;

  async function _doRefresh() {
    if (refreshInflight) {
      refreshPending = true;
      return;
    }
    refreshInflight = true;
    try {
      const cartIds = await getCartVariantIds();
      const buttons = Array.from(
        document.querySelectorAll(
          '.add-to-cart-button, button[name="add"], .product-form__submit'
        )
      ).filter((btn) => !btn.closest('.cart-drawer, cart-drawer-component, dialog.cart-drawer__dialog'));
      buttons.forEach((btn) => {
        const id = getButtonVariantId(btn);
        applyState(btn, !!id && cartIds.has(id));
      });
    } finally {
      refreshInflight = false;
      if (refreshPending) {
        refreshPending = false;
        refreshButtons();
      }
    }
  }

  function refreshButtons() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(_doRefresh, 200);
  }

  function openCartDrawer() {
    const trigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (trigger) {
      trigger.click();
      return true;
    }
    const dlg = document.querySelector('dialog.cart-drawer__dialog');
    if (dlg && typeof dlg.showModal === 'function' && !dlg.open) {
      try {
        dlg.showModal();
        return true;
      } catch {
        /* ignore */
      }
    }
    return false;
  }

  function installClickGuard() {
    document.addEventListener(
      'click',
      (e) => {
        const btn = e.target.closest('.add-to-cart-button, button[name="add"], .product-form__submit');
        if (!btn) return;

        const inCart = btn.classList.contains(IN_CART_CLASS) || btn.getAttribute('aria-disabled') === 'true';
        if (inCart) {
          e.preventDefault();
          e.stopImmediatePropagation();
          if (btn.classList.contains('quick-add__button')) {
            openCartDrawer();
          }
          return;
        }

        const isQuickAdd = btn.classList.contains('quick-add__button');

        // PDP / sticky ADD TO CART: open drawer after successful add.
        if (!isQuickAdd) {
          const onceCartUpdate = () => {
            document.removeEventListener('cart:update', onceCartUpdate);
            setTimeout(openCartDrawer, 50);
          };
          document.addEventListener('cart:update', onceCartUpdate);
          setTimeout(() => document.removeEventListener('cart:update', onceCartUpdate), 3000);
        }

        // Optimistic UI for quick-add: mark as in-cart immediately (after the theme
        // handler runs in capture phase). If the fetch errors, cart:error invalidates.
        if (isQuickAdd) {
          const variantId = getButtonVariantId(btn);
          if (variantId) {
            requestAnimationFrame(() => {
              const next = new Set(getCachedCartIds());
              next.add(variantId);
              cachedCartIds = next;
              // Mark this button + any sibling buttons for the same variant.
              document
                .querySelectorAll('.add-to-cart-button, button[name="add"], .product-form__submit')
                .forEach((b) => {
                  if (b.closest('.cart-drawer, cart-drawer-component, dialog.cart-drawer__dialog')) return;
                  if (getButtonVariantId(b) === variantId) applyState(b, true);
                });
            });
          }
        }
      },
      true
    );
  }

  function blockedResponse() {
    const body = JSON.stringify({ status: 422, message: IN_CART_TEXT, description: IN_CART_TEXT });
    return new Response(body, {
      status: 422,
      statusText: 'Unprocessable Entity',
      headers: { 'Content-Type': 'application/json' },
    });
  }

  function tryUpdateCacheFromResponseClone(resClone) {
    // Best-effort: parse the cart mutation response.
    // /cart/change.js/update.js returns full cart with items[].
    // /cart/add.js returns the added item (no items[]); fall back to invalidate.
    resClone
      .json()
      .then((data) => {
        if (data && Array.isArray(data.items)) {
          cachedCartIds = new Set(data.items.map((i) => String(i.variant_id)));
        } else {
          cachedCartIds = null;
        }
        refreshButtons();
      })
      .catch(() => {
        cachedCartIds = null;
        refreshButtons();
      });
  }

  window.fetch = async function patchedFetch(input, init) {
    const url = getUrlString(input);

    if (isCartAddUrl(url)) {
      const variantId = extractVariantId(init);
      if (variantId && getCachedCartIds().has(variantId)) {
        // silently block (uses in-memory cache, no extra fetch)
        refreshButtons();
        return blockedResponse();
      }
    }

    const res = await originalFetch(input, init);

    if (isCartMutationUrl(url)) {
      try {
        tryUpdateCacheFromResponseClone(res.clone());
      } catch {
        cachedCartIds = null;
        refreshButtons();
      }
    }

    return res;
  };

  installClickGuard();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', refreshButtons, { once: true });
  } else {
    refreshButtons();
  }

  // Refresh on variant change (radio / select inside the form).
  document.addEventListener('change', (e) => {
    if (!e.target.closest('form[action*="/cart/add"]')) return;
    setTimeout(refreshButtons, 30);
  });

  // Theme dispatches 'cart:update' on every cart mutation.
  document.addEventListener('cart:update', () => {
    cachedCartIds = null;
    refreshButtons();
  });
  document.addEventListener('cart:error', () => {
    cachedCartIds = null;
    refreshButtons();
  });
  // Re-apply state after section reloads (cards re-rendered by Section Rendering API).
  document.addEventListener('shopify:section:load', refreshButtons);
})();
